/**
 * Management drawer for a team / project scope group.
 *
 * Edit name + description inline, flip member roles, add/remove members
 * (picked from the admin users list), and delete the group. Deletes blocked
 * server-side (shared-LTM files / cron jobs still referencing the scope)
 * surface the 409 detail as a toast — the server owns the blocker check.
 */
import { useMemo, useState } from "react";

import {
  adminApi,
  type AdminScopeGroupDetail,
  type AdminScopeGroupType,
} from "../../api/admin";
import { EditableField } from "../../components/EditableField";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Drawer } from "../../components/admin/Drawer";
import { useConfirm } from "../../components/confirm/context";
import { useToast } from "../../components/toast/context";
import { formatRelative } from "../../lib/formatRelative";

const ROLES = ["member", "lead"] as const;

interface Props {
  typeName: AdminScopeGroupType;
  groupId: string | null;
  onClose: () => void;
}

export function ScopeGroupDrawer({ typeName, groupId, onClose }: Props) {
  return (
    <Drawer
      open={!!groupId}
      onClose={onClose}
      eyebrow="Admin"
      title={typeName === "team" ? "Team detail" : "Project detail"}
      widthClass="w-[620px] max-w-full"
    >
      {groupId ? (
        <DrawerBody typeName={typeName} groupId={groupId} onClose={onClose} />
      ) : null}
    </Drawer>
  );
}

function DrawerBody({
  typeName,
  groupId,
  onClose,
}: {
  typeName: AdminScopeGroupType;
  groupId: string;
  onClose: () => void;
}) {
  const groupQuery = adminApi.scopes.group(typeName, groupId);
  const patchMutation = adminApi.scopes.patchGroup(typeName, groupId);
  const deleteMutation = adminApi.scopes.deleteGroup(typeName);
  const putMember = adminApi.scopes.putMember(typeName, groupId);
  const removeMember = adminApi.scopes.removeMember(typeName, groupId);
  const toast = useToast();
  const confirm = useConfirm();

  const onDeleteGroup = async (detail: AdminScopeGroupDetail) => {
    const ok = await confirm({
      title: `Delete ${typeName} '${detail.name}' (${detail.slug})?`,
      body: "Members lose the scope. Cron jobs and shared LTM must already be cleaned up.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    deleteMutation.mutate(groupId, {
      onSuccess: () => onClose(),
      onError: (e) => toast.error(e),
    });
  };

  return (
    <>
      <QueryBoundary query={groupQuery}>
        {(detail) => (
          <div className="space-y-5">
            <div className="text-sm text-ink-medium space-y-1">
              <div>
                <span className="text-ink-low">Slug:</span>{" "}
                <span className="tag-chip">{detail.slug}</span>
              </div>
              <div>
                <span className="text-ink-low">ID:</span>{" "}
                <span className="tnum">{detail.id}</span>
              </div>
              {detail.created_at ? (
                <div className="text-ink-low">
                  Created {formatRelative(detail.created_at)}
                </div>
              ) : null}
            </div>

            <section className="panel panel--flush">
              <div className="panel-header">
                <h3>Profile</h3>
              </div>
              <div
                className="panel-body grid gap-4"
                style={{ gridTemplateColumns: "minmax(120px, 0.4fr) 1fr" }}
              >
                <FieldLabel>Name</FieldLabel>
                <EditableField
                  value={detail.name}
                  label="Name"
                  onSave={async (next) => {
                    if (!next || next === detail.name) return;
                    await patchMutation.mutateAsync({ name: next });
                    toast.ok("Renamed");
                  }}
                />
                <FieldLabel>Description</FieldLabel>
                <EditableField
                  value={detail.description ?? ""}
                  label="Description"
                  variant="textarea"
                  // Send the raw string — "" overwrites (clears) server-side,
                  // null would be treated as "no change".
                  onSave={async (next) =>
                    patchMutation.mutateAsync({ description: next })
                  }
                />
              </div>
            </section>

            <MembersSection
              detail={detail}
              onSetRole={(userId, role) =>
                putMember.mutate(
                  { userId, role },
                  {
                    onSuccess: (m) => toast.ok(`${m.email} → ${m.role}`),
                    onError: (e) => toast.error(e),
                  },
                )
              }
              onRemove={async (member) => {
                const ok = await confirm({
                  title: `Remove ${member.email} from ${typeName} '${detail.name}'?`,
                  confirmLabel: "Remove",
                  tone: "danger",
                });
                if (!ok) return;
                removeMember.mutate(member.user_id, {
                  onSuccess: () => toast.ok("Member removed"),
                  onError: (e) => toast.error(e),
                });
              }}
            />

            <div className="flex justify-end pt-2 border-t border-rule">
              <button
                type="button"
                className="btn btn-ghost btn-danger"
                disabled={deleteMutation.isPending}
                onClick={() => onDeleteGroup(detail)}
              >
                {deleteMutation.isPending
                  ? "Deleting…"
                  : `Delete ${typeName}`}
              </button>
            </div>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

function MembersSection({
  detail,
  onSetRole,
  onRemove,
}: {
  detail: AdminScopeGroupDetail;
  onSetRole: (userId: string, role: string) => void;
  onRemove: (member: AdminScopeGroupDetail["members"][number]) => void;
}) {
  const usersQuery = adminApi.users.list();
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState<string>("member");

  const candidates = useMemo(() => {
    const memberIds = new Set(detail.members.map((m) => m.user_id));
    return (usersQuery.data ?? []).filter(
      (u) => !memberIds.has(u.id) && !u.disabled_at,
    );
  }, [usersQuery.data, detail.members]);

  return (
    <section className="panel panel--flush">
      <div className="panel-header">
        <h3>Members</h3>
        <span className="text-xs text-ink-low tnum">
          {detail.members.length}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {detail.members.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-sm text-ink-low">
                  No members yet.
                </td>
              </tr>
            ) : (
              detail.members.map((member) => (
                <tr key={member.user_id}>
                  <td>
                    <span className="font-heading font-semibold text-ink">
                      {member.email}
                    </span>
                  </td>
                  <td className="text-sm text-ink-medium">
                    {member.name || "—"}
                  </td>
                  <td>
                    <select
                      className="input"
                      aria-label={`Role for ${member.email}`}
                      value={member.role}
                      onChange={(e) =>
                        onSetRole(member.user_id, e.target.value)
                      }
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onRemove(member)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="panel-body border-t border-rule">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="label" htmlFor="scope-member-add">
              Add member
            </label>
            <select
              id="scope-member-add"
              className="input"
              value={addUserId}
              onChange={(e) => setAddUserId(e.target.value)}
            >
              <option value="">Select user…</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="scope-member-role">
              Role
            </label>
            <select
              id="scope-member-role"
              className="input"
              value={addRole}
              onChange={(e) => setAddRole(e.target.value)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-second"
            disabled={!addUserId}
            onClick={() => {
              onSetRole(addUserId, addRole);
              setAddUserId("");
              setAddRole("member");
            }}
          >
            Add
          </button>
        </div>
      </div>
    </section>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-heading font-semibold text-ink-low pt-2">
      {children}
    </span>
  );
}
